<template>
    <validation-observer v-slot="{ handleSubmit }" slim>
        <form class="signup-form" @submit.prevent="handleSubmit(submit)">
            <p v-if="prefilled" class="m-0">
                We filled in what {{ providerLabel }} shared with us. Complete the rest to continue.
            </p>

            <store-fields
                :store-type="form.storeType"
                :store-url="form.storeUrl"
                :lock-store-type="lockStoreType"
                @update:storeType="onStoreTypeChange"
                @update:storeUrl="form.storeUrl = $event"
            />

            <!-- Platforms with their own app store onboard from there -->
            <div v-if="appStoreListing" class="text-center">
                <p>Find us on the {{ appStoreListing.name }} App Store:</p>
                <a
                    class="btn btn-primary w-100 font-weight-normal"
                    :href="appStoreListing.url"
                    target="_blank"
                    rel="noopener"
                >
                    Install Customily
                </a>
            </div>

            <!-- Platforms whose store comes from their own sign in -->
            <div v-else-if="providerSignUp" class="text-center">
                <p>Sign up with your {{ providerName }} account to connect your store:</p>
                <social-auth-button
                    :provider="providerSignUp"
                    :label="`Sign up with ${providerName}`"
                    :loading="providerLoading"
                    @click="emit('provider-sign-up', providerSignUp)"
                />
            </div>

            <template v-else>
                <validation-provider v-slot="{ errors, failed }" rules="required" name="Company Name">
                    <c-input
                        v-model="form.name"
                        label="Company Name*"
                        placeholder="Type your company name"
                        :error="failed"
                        :error-msg="errors[0]"
                    />
                </validation-provider>

                <div class="signup-form-row">
                    <!-- Suggested provider usernames are slugs, which the external sign up accepts -->
                    <validation-provider
                        v-slot="{ errors, failed }"
                        :rules="prefilled ? 'required|alpha_dash' : 'required|alpha_num'"
                        name="Username"
                    >
                        <c-input
                            v-model="form.username"
                            label="Username*"
                            placeholder="Type your username"
                            :error="failed"
                            :error-msg="errors[0]"
                        />
                    </validation-provider>
                    <validation-provider v-slot="{ errors, failed }" rules="required|email" name="Email">
                        <c-input
                            v-model="form.email"
                            type="email"
                            label="Email*"
                            placeholder="Type your email"
                            :disabled="!!prefilled"
                            :error="failed"
                            :error-msg="errors[0]"
                        />
                    </validation-provider>
                </div>

                <!-- A third party sign up has no password of its own: the user sets one through forgot password -->
                <template v-if="!prefilled">
                    <div class="signup-form-row">
                        <validation-provider
                            v-slot="{ errors, failed }"
                            rules="required|min:8"
                            name="Password"
                            vid="password"
                        >
                            <c-input
                                v-model="form.password"
                                type="password"
                                label="Password*"
                                placeholder="Type your password"
                                :error="failed"
                                :error-msg="errors[0]"
                            />
                        </validation-provider>
                        <validation-provider
                            v-slot="{ errors, failed }"
                            rules="required|min:8|password-confirmed:password"
                            name="Password confirmation"
                        >
                            <c-input
                                v-model="form.confirmationPassword"
                                type="password"
                                label="Confirm Password*"
                                placeholder="Re-enter your password"
                                :error="failed"
                                :error-msg="errors[0]"
                            />
                        </validation-provider>
                    </div>

                    <validation-provider v-slot="{ errors, failed }" rules="required" name="Captcha">
                        <c-recaptcha ref="recaptchaRef" v-model="captchaToken" :error="failed" :error-msg="errors[0]" />
                    </validation-provider>
                </template>
            </template>

            <c-error v-if="errorMessage" :message="errorMessage" />

            <div class="form-actions" :class="{ 'single-action': hasOwnOnboarding }">
                <c-button variant="light" text="Back" :disabled="submitting || providerLoading" @click="emit('back')" />
                <c-button
                    v-if="!hasOwnOnboarding"
                    type="submit"
                    variant="primary"
                    :text="submitting ? 'Signing up...' : 'Sign up'"
                    :disabled="submitting"
                />
            </div>
        </form>
    </validation-observer>
</template>

<script setup lang="ts">
import { computed, reactive, ref } from 'vue';
import { ValidationObserver, ValidationProvider } from 'vee-validate';
import CButton from '@/components/shared/CButton/CButton.vue';
import CError from '@/components/shared/CError.vue';
import CInput from '@/components/shared/CInput/CInput.vue';
import CRecaptcha, { type CRecaptchaRef } from '@/components/shared/CRecaptcha';
import StoreFields from '@/components/Auth/StoreFields.vue';
import SocialAuthButton from '@/components/Auth/SocialAuthButton.vue';
import { completeExternalSignUp } from '@/api/auth';
import { registerUser } from '@/api/user';
import { externalAuthErrorMessage, toRegistrationStoreUrl } from '@/utils/authUtils';
import { toRegistrationErrorMessage } from '@/views/SignUp/registrationError';
import { appStoreListings, providerSignUps } from '@/views/SignUp/storeTypes';
import type { AxiosError } from 'axios';
import { externalProviderLabels, type ExternalAuthResult, type ExternalProvider } from '@/typings/auth/externalAuth';

const props = defineProps<{
    storeType: string;
    lockStoreType?: boolean;
    /** Partner that manages the account (`mngBy` query param), sent along with the manual sign up. */
    managedBy?: string;
    /** Set when a provider already verified the identity: then the account is created from its ticket. */
    prefilled?: { provider: ExternalProvider; pendingTicket: string; email: string; username?: string };
    /** A provider sign up started from this form is running. */
    providerLoading?: boolean;
}>();

const emit = defineEmits<{
    (event: 'back'): void;
    (event: 'update:storeType', storeType: string): void;
    (event: 'registered', username: string, storeType: string): void;
    (event: 'completed', result: ExternalAuthResult): void;
    (event: 'provider-sign-up', provider: ExternalProvider): void;
}>();

const prefilled = computed(() => props.prefilled);

// The page shows the pitch of the selected platform
const onStoreTypeChange = (storeType: string) => {
    form.storeType = storeType;
    emit('update:storeType', storeType);
};
const providerLabel = computed(() => externalProviderLabels[props.prefilled?.provider || 'google']);

const captchaToken = ref('');
const recaptchaRef = ref<CRecaptchaRef>();

const submitting = ref(false);
const errorMessage = ref('');
const form = reactive({
    storeType: props.storeType,
    storeUrl: '',
    name: '',
    username: props.prefilled?.username || '',
    email: props.prefilled?.email || '',
    password: '',
    confirmationPassword: ''
});

const appStoreListing = computed(() => appStoreListings[form.storeType]);
const providerSignUp = computed(() => providerSignUps[form.storeType]);
const providerName = computed(() => (providerSignUp.value ? externalProviderLabels[providerSignUp.value] : ''));
// The store comes from elsewhere: no store, account or password fields here
const hasOwnOnboarding = computed(() => !!appStoreListing.value || !!providerSignUp.value);

const completePrefilledSignUp = async (pendingTicket: string): Promise<boolean> => {
    try {
        emit(
            'completed',
            await completeExternalSignUp({
                pendingTicket,
                storeType: form.storeType,
                storeUrl: toRegistrationStoreUrl(form.storeType, form.storeUrl.trim()),
                companyName: form.name.trim(),
                username: form.username.trim()
            })
        );
        return true;
    } catch (error) {
        errorMessage.value = externalAuthErrorMessage(error, 'We could not create your account. Please try again.');
        return false;
    }
};

const registerManually = async (): Promise<boolean> => {
    try {
        await registerUser({
            Name: form.name.trim(),
            Username: form.username.trim(),
            Email: form.email.trim(),
            Password: form.password,
            ConfirmationPassword: form.confirmationPassword,
            StoreType: form.storeType,
            StoreUrl: form.storeUrl.trim(),
            Captcha: captchaToken.value,
            ManagedBy: props.managedBy
        });
        emit('registered', form.username.trim(), form.storeType);
        return true;
    } catch (error) {
        recaptchaRef.value?.reset();
        errorMessage.value = toRegistrationErrorMessage(error as AxiosError);
        return false;
    }
};

const submit = async () => {
    errorMessage.value = '';
    submitting.value = true;
    const callback = prefilled.value
        ? () => completePrefilledSignUp(prefilled.value!.pendingTicket)
        : registerManually;
    const created = await callback();
    // On success the page is leaving for Stripe and the ticket is spent: a second click would only fail
    if (!created) submitting.value = false;
};
</script>

<style lang="scss" scoped>
.signup-form {
    display: flex;
    flex-direction: column;
    gap: 1.25rem;
}

.signup-form-row {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 0.5rem;
}

.form-actions {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 1.25rem;
    &.single-action {
        grid-template-columns: 1fr;
    }
}

@media (max-width: 575px) {
    .signup-form-row {
        grid-template-columns: 1fr;
    }
}
</style>
